import { useEffect, useRef } from 'react';
import { CompactPicker } from 'react-color';
import { useUnityBuildStore } from '../../stores/store';
import { useSubmitTakeoffAssembly } from '../../hooks/useSubmitTakeoffAssembly';
import { mutateSelectedAssembly } from '../../api/MutateSelectedAssembly';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../stores/AssemblyStore';

export const ColorPicker = () => {
  const isEditingPointFillColor = useStore(useUnityBuildStore).isEditingPointFillColor;
  const isEditingPointBorderColor = useStore(useUnityBuildStore).isEditingPointBorderColor;
  const isEditingLineColor = useStore(useUnityBuildStore).isEditingLineColor;
  const backgroundColor = useStore(useUnityBuildStore).backgroundColor;
  const userScale = useStore(useUnityBuildStore).userScale;
  const setBackgroundColor = useStore(useUnityBuildStore).setBackgroundColor;
  const currentTakeoffAssembly = useStore(useAssemblyStore).currentTakeoffAssembly;
  const setCurrentTakeoffAssembly = useStore(useAssemblyStore).setCurrentTakeoffAssembly;

  const { submitAssembly } = useSubmitTakeoffAssembly();

  const currentAssemblyRef = useRef(currentTakeoffAssembly);

  useEffect(() => {
    currentAssemblyRef.current = currentTakeoffAssembly;
  }, [currentTakeoffAssembly]);

  useEffect(() => {
    if (isEditingPointFillColor) {
      setBackgroundColor(currentAssemblyRef.current?.pointfillcolor || '#BD10E0');
    } else if (isEditingPointBorderColor) {
      setBackgroundColor(currentAssemblyRef.current?.pointbordercolor || '#BD10E0');
    } else if (isEditingLineColor) {
      setBackgroundColor(currentAssemblyRef.current?.linecolor || '#BD10E0');
    }
  }, [setBackgroundColor, isEditingPointBorderColor, isEditingPointFillColor, isEditingLineColor]);

  const handleOnChangeComplete = async (color: any) => {
    if (!currentAssemblyRef.current) {
      console.error('No assembly selected');
      return;
    }
    setBackgroundColor(color.hex);

    // Optimistic update - immediately update the store for instant feedback
    if (currentTakeoffAssembly) {
      const updates: Partial<typeof currentTakeoffAssembly> = {};

      if (isEditingPointFillColor) {
        updates.pointfillcolor = color.hex;
      } else if (isEditingPointBorderColor) {
        updates.pointbordercolor = color.hex;
      } else if (isEditingLineColor) {
        updates.linecolor = color.hex;
      }

      setCurrentTakeoffAssembly({
        ...currentTakeoffAssembly,
        ...updates,
      });
    }

    // Background API call
    const updatedAssembly = mutateSelectedAssembly({
      assembly: currentAssemblyRef.current,
      color: color.hex,
      isEditingPointBorderColor,
      isEditingPointFillColor,
      isEditingLineColor,
    });

    submitAssembly({
      shouldUpdateBreakout: false,
      assembly: updatedAssembly,
      isUpdating: true,
      pageScale: userScale,
      shouldClearCurrentTakeoffAssembly: false,
    }).catch(console.error);
  };

  return (
    <div style={{ width: '100%', marginTop: '10px', marginBottom: '10px' }}>
      {(isEditingPointFillColor || isEditingPointBorderColor || isEditingLineColor) && (
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
        >
          {isEditingPointFillColor && 'Select Fill Color'}
          {isEditingPointBorderColor && 'Select Border Color'}
          {isEditingLineColor && 'Select Line Color'}
        </div>
      )}
      <CompactPicker
        color={backgroundColor}
        onChangeComplete={handleOnChangeComplete}
        width="100%"
        styles={{
          default: {
            compact: {
              width: '100%',
            },
          },
        }}
      />
    </div>
  );
};
